import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { jwtDecode } from 'jwt-decode';

/** Services, constants, interface */
import { userService } from "./userAPI";
import { DeleteResponse, LoggedInUser, LoggedInUserDefaultData, UserData } from "./user.types";
import { DEFAULT_LANGUAGE } from '../../utils/constants';


interface UserState {
  loginUser: LoggedInUser,
  users: UserData[];
  sites: any[];
  loading: boolean;
  error: string | null;
}

const initialState: UserState = {
  loginUser : LoggedInUserDefaultData,
  sites: [],
  users: [],
  loading: false,
  error: null,
};

type CreateUserPayload = {
  user: UserData;
  loginUserData: LoggedInUser;
}

// Thunks
export const fetchUsers = createAsyncThunk('users/fetch',
  async (loginUserData :LoggedInUser, thunkAPI) => {
    try {
      return await userService.fetchUsers(loginUserData);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  });

export const createUser = createAsyncThunk(
  'users/create',
  async ({ user, loginUserData }: CreateUserPayload, thunkAPI) => {
    try {
      return await userService.addUser(user,loginUserData);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/update',
  async ({ user, loginUserData }: CreateUserPayload, thunkAPI) => {
    try {
      return await userService.updateUser(user,loginUserData);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/delete',
  async (users: UserData[], thunkAPI) => {
    try {
      return await userService.deleteUser(users,true);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const bulkDeleteUser = createAsyncThunk(
  'users/builDelete',
  async (users: UserData[], thunkAPI) => {
    try {
      return await userService.bulkDeleteUser(users,true);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    // Local (synchronous) reducers
    setLoggedInUser(state, action: PayloadAction<LoggedInUser>) {
      let userData : LoggedInUser = action.payload;
      if(!userData.preferredLanguage){
        userData.preferredLanguage = DEFAULT_LANGUAGE;
      }
      state.loginUser = userData;
    },

    setSites(state, action: PayloadAction<any[]>) {
      state.sites = action.payload;
    },

    clearLoginUser(state) {
      state.loginUser = LoggedInUserDefaultData;
    },

    addUserTemp(state, action: PayloadAction<UserData>) {
      state.users.push(action.payload);
    },

    updateTempUser(state, action: PayloadAction<UserData>) {
       const index = state.users.findIndex(u => u.id === action.payload.id);
       state.users[index] = action.payload;
    },

    deleteUserTemp(state, action: PayloadAction<UserData[]>) {
      const idsToDelete = new Set(action.payload.map(user => user.id));
      state.users = state.users.filter(user => !idsToDelete.has(user.id));
    },

    initUserFromToken(state) {
      const token = localStorage.getItem("access_token");
      if (token) {
        let userData : any = jwtDecode(token);
        if(!userData.preferredLanguage){
          userData.preferredLanguage = DEFAULT_LANGUAGE;
        }
        state.loginUser = userData;
      }
    },
  },
  extraReducers: builder => {
    // These handle async actions (like fetchUsers thunk)
    builder
      .addCase(fetchUsers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<UserData[]>) => {
        state.users = action.payload;
        state.loading = false;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.error = action.error.message ?? 'Error';
        state.loading = false;
      })
      .addCase(createUser.fulfilled, (state, action: PayloadAction<UserData>) => {
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUser.fulfilled, (state, action: PayloadAction<UserData>) => {
        const index = state.users.findIndex(u => u.id === action.payload.id);
        if (index !== -1) state.users[index] = action.payload;
      })
      .addCase(deleteUser.fulfilled, (state, action: PayloadAction<UserData>) => {
        state.users = state.users.filter(u => u.id !== action.payload.id);
      })
      .addCase(bulkDeleteUser.fulfilled, (state, action: PayloadAction<DeleteResponse[]>) => {
        // Get IDs of users successfully deleted
        const deletedIds = action.payload
          .filter((res: DeleteResponse) => res.success)
          .map((res: DeleteResponse) => res.userId);
         state.users = state.users.filter((u) => !deletedIds.includes(u.id));
      });
  }
});

export const { addUserTemp, deleteUserTemp, setLoggedInUser, updateTempUser, clearLoginUser, initUserFromToken, setSites } = userSlice.actions;

export default userSlice.reducer;